/**
 * Link giao ảnh cho khách.
 *
 * Hai nửa, hai quyền khác nhau: nửa nhân viên đi qua client đã đăng nhập, nửa
 * khách đi qua `fetch` trần — màn khách không có phiên đăng nhập nào, token
 * trên đường dẫn là quyền duy nhất.
 */
import { api } from "@/lib/api/client";
import { serverErrorMessage } from "@/lib/api/error-message";
import type { components } from "@/lib/api/generated/schema";

export type AlbumShare = components["schemas"]["AlbumShare"];
export type AlbumShareCreated = components["schemas"]["AlbumShareCreated"];
export type CustomerAlbum = components["schemas"]["CustomerAlbum"];
export type PhotoSelectionAlbum = components["schemas"]["PhotoSelectionAlbum"];

/** Ba loại link, ba màn khách khác nhau. Xem `scope` ở openapi.yaml. */
export type ShareScope = "final" | "select" | "print";
export type CustomerAlbumZipInput =
  | { token: string; orderId?: never; source?: never; filename: string }
  | { orderId: string; token?: never; source?: "final" | "original"; filename: string };

const apiBaseUrl = import.meta.env.VITE_API_URL ?? "http://localhost:8080/api/v1";

export const albumShareKeys = {
  byOrder: (orderId: string) => ["album-shares", orderId] as const,
  customer: (token: string) => ["album-share-customer", token] as const,
  customerOrder: (orderId: string) => ["album-share-customer-order", orderId] as const,
  customerOrderOriginal: (orderId: string) => ["album-share-customer-order-original", orderId] as const,
  selection: (token: string) => ["photo-selection", token] as const,
};

function shareError(payload: unknown, fallback: string): Error {
  return new Error(serverErrorMessage(payload, fallback));
}

/** Tạo link. `token` chỉ về đúng lần này — máy chủ không trả lại lần thứ hai. */
export async function createAlbumShare(
  orderId: string,
  expiresInHours?: number,
  scope: ShareScope = "final",
): Promise<AlbumShareCreated> {
  const { data, error } = await api.POST("/orders/{id}/album-shares", {
    params: { path: { id: orderId } },
    body: {
      ...(expiresInHours ? { expiresInHours } : {}),
      scope,
    },
  });
  if (error || !data) throw shareError(error, "Không tạo được link");
  return data;
}

/** CSKH mở lại cho khách chọn tiếp sau khi khách đã chốt. */
export async function reopenPhotoSelection(orderId: string): Promise<void> {
  const { error, response } = await api.POST("/orders/{id}/photo-selection/reopen", {
    params: { path: { id: orderId } },
  });
  if (!response.ok) throw shareError(error, "Không mở lại được cho khách chọn");
}

/**
 * CSKH mở lại cho khách XEM ảnh gốc thêm 24 giờ (quyết định 0106).
 *
 * Khác `reopenPhotoSelection` một chữ nhưng khác hẳn việc: nút kia mở quyền
 * CHỌN, nút này mở quyền NHÌN. Khách hết 48 giờ xem ảnh gốc thì gọi lên, nhân
 * viên bấm cái này.
 *
 * Trả hạn mới để màn nói được "đến mấy giờ" thay vì "đã mở lại" chung chung.
 */
export async function reopenOriginalAlbum(orderId: string): Promise<string> {
  const { data, error, response } = await api.POST("/orders/{id}/original-album/reopen", {
    params: { path: { id: orderId } },
  });
  if (!response.ok || !data) throw shareError(error, "Không mở lại được ảnh gốc cho khách");
  return data.deadline;
}

/** Danh sách link đã tạo, kèm trạng thái còn sống / hết hạn / đã thu hồi. */
export async function listAlbumShares(orderId: string): Promise<AlbumShare[]> {
  const { data, error } = await api.GET("/orders/{id}/album-shares", {
    params: { path: { id: orderId } },
  });
  if (error || !data) throw shareError(error, "Không tải được danh sách link");
  return data;
}

/** Thu hồi: link chết ngay, không đợi hết hạn. */
export async function revokeAlbumShare(orderId: string, shareId: string): Promise<AlbumShare> {
  const { data, error } = await api.DELETE("/orders/{id}/album-shares/{shareId}", {
    params: { path: { id: orderId, shareId } },
  });
  if (error || !data) throw shareError(error, "Không thu hồi được link");
  return data;
}

/**
 * [Khách] Mở album bằng token.
 *
 * Không qua client đã đăng nhập: màn này chạy cho người không có tài khoản, và
 * gắn credential vào chỉ tổ gửi cookie phiên của nhân viên nào đó vừa dùng
 * chung máy.
 */
export async function openCustomerAlbum(token: string): Promise<CustomerAlbum> {
  const resp = await fetch(`${apiBaseUrl}/album-shares/${encodeURIComponent(token)}`, {
    headers: { Accept: "application/json" },
  });
  if (!resp.ok) {
    // Máy chủ cố tình trả cùng một 404 cho token sai / hết hạn / đã thu hồi.
    // FE giữ nguyên cách nói đó, không đoán hộ khách lý do nào.
    if (resp.status === 404) {
      throw new Error(
        "Link này không còn xem được. Chị liên hệ studio để được gửi lại link mới giúp em ạ.",
      );
    }
    if (resp.status === 429) {
      throw new Error("Nhiều lượt xem quá, chị đợi một chút rồi mở lại giúp em ạ.");
    }
    throw new Error("Không mở được album, chị thử lại giúp em ạ.");
  }
  return (await resp.json()) as CustomerAlbum;
}

/** [Khách đã đăng nhập SĐT] Mở album ngay từ trang booking/me theo order id. */
export async function openCustomerOrderAlbum(orderId: string): Promise<CustomerAlbum> {
  const resp = await fetch(
    `${apiBaseUrl}/public/customer-orders/${encodeURIComponent(orderId)}/album`,
    {
      credentials: "include",
      headers: { Accept: "application/json" },
    },
  );
  if (!resp.ok) {
    if (resp.status === 401) {
      throw new Error("Chị đăng nhập lại bằng số điện thoại để xem album giúp em ạ.");
    }
    if (resp.status === 404) {
      throw new Error(
        "Album này chưa xem trực tiếp được. Chị liên hệ studio để được hỗ trợ giúp em ạ.",
      );
    }
    if (resp.status === 429) {
      throw new Error("Nhiều lượt xem quá, chị đợi một chút rồi mở lại giúp em ạ.");
    }
    throw new Error("Không mở được album, chị thử lại giúp em ạ.");
  }
  return (await resp.json()) as CustomerAlbum;
}

/** [Khách đã đăng nhập SĐT] Mở ảnh gốc đã upload của đơn ngay từ /booking/me. */
export async function openCustomerOrderOriginalAlbum(orderId: string): Promise<CustomerAlbum> {
  const resp = await fetch(
    `${apiBaseUrl}/public/customer-orders/${encodeURIComponent(orderId)}/original-album`,
    {
      credentials: "include",
      headers: { Accept: "application/json" },
    },
  );
  if (!resp.ok) {
    if (resp.status === 401) {
      throw new Error("Chị đăng nhập lại bằng số điện thoại để xem ảnh gốc giúp em ạ.");
    }
    if (resp.status === 404) {
      throw new Error(
        "Ảnh gốc của đơn này chưa xem trực tiếp được. Chị liên hệ studio để được hỗ trợ giúp em ạ.",
      );
    }
    if (resp.status === 429) {
      throw new Error("Nhiều lượt xem quá, chị đợi một chút rồi mở lại giúp em ạ.");
    }
    throw new Error("Không mở được ảnh gốc, chị thử lại giúp em ạ.");
  }
  return (await resp.json()) as CustomerAlbum;
}

export async function downloadCustomerAlbumZip(input: CustomerAlbumZipInput): Promise<void> {
  const resp = await fetch(customerAlbumZipUrl(input), {
    credentials: input.orderId ? "include" : "same-origin",
    headers: { Accept: "application/zip" },
  });
  if (!resp.ok) {
    if (resp.status === 401) {
      throw new Error("Chị đăng nhập lại bằng số điện thoại để tải album giúp em ạ.");
    }
    if (resp.status === 404) {
      throw new Error("Album này chưa tải được. Chị liên hệ studio để được hỗ trợ giúp em ạ.");
    }
    if (resp.status === 429) {
      throw new Error("Nhiều lượt tải quá, chị đợi một chút rồi thử lại giúp em ạ.");
    }
    throw new Error("Không tải được album, chị thử lại giúp em ạ.");
  }

  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = input.filename;
  document.body.append(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function customerAlbumZipUrl(input: CustomerAlbumZipInput): string {
  if ("token" in input) {
    return `${apiBaseUrl}/album-shares/${encodeURIComponent(input.token)}/download.zip`;
  }
  // Ảnh GỐC không có đường tải — cửa /original-album.zip đã gỡ ở máy chủ
  // (quyết định 0106). Ném ở đây thay vì gọi rồi nhận 404: một nút tải chỉ
  // báo "không tải được album" thì không ai hiểu là tính năng đã bỏ hẳn.
  if (input.source === "original") {
    throw new Error("Ảnh gốc chỉ xem tại đây, không tải về được ạ.");
  }
  return `${apiBaseUrl}/public/customer-orders/${encodeURIComponent(input.orderId)}/album.zip`;
}

// ------------------------------------------------------- khách chọn ảnh

/**
 * Gọi đường công khai của màn chọn ảnh.
 *
 * Không qua client đã đăng nhập, cùng lý do với `openCustomerAlbum`: màn này
 * chạy cho người không có tài khoản, gắn credential vào chỉ tổ gửi cookie phiên
 * của nhân viên nào đó vừa dùng chung máy.
 */
async function selectionFetch(
  token: string,
  path: string,
  init?: { method: string; body?: unknown },
): Promise<PhotoSelectionAlbum> {
  const resp = await fetch(`${apiBaseUrl}/photo-selection/${encodeURIComponent(token)}${path}`, {
    method: init?.method ?? "GET",
    headers: {
      Accept: "application/json",
      ...(init?.body !== undefined ? { "Content-Type": "application/json" } : {}),
    },
    ...(init?.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
  });
  if (!resp.ok) throw await selectionError(resp);
  return (await resp.json()) as PhotoSelectionAlbum;
}

async function selectionError(resp: Response): Promise<Error> {
  // Máy chủ trả cùng một 404 cho token sai / hết hạn / thu hồi / sai phạm vi.
  // FE giữ nguyên cách nói đó, không đoán hộ khách lý do nào.
  if (resp.status === 404) {
    return new Error(
      "Link này không còn dùng được. Chị liên hệ studio để được gửi lại link mới giúp em ạ.",
    );
  }
  if (resp.status === 429) {
    return new Error("Chị thao tác hơi nhanh, đợi một chút rồi thử lại giúp em ạ.");
  }
  let body: unknown;
  try {
    body = await resp.json();
  } catch {
    // Thân không phải JSON — rơi xuống câu chung.
  }
  return new Error(serverErrorMessage(body, "Không thực hiện được, chị thử lại giúp em ạ."));
}

/** Mở màn chọn ảnh. */
export function openPhotoSelection(token: string): Promise<PhotoSelectionAlbum> {
  return selectionFetch(token, "");
}

/** Tick hoặc bỏ tick một ảnh. */
export function selectPhoto(
  token: string,
  photoId: string,
  selected: boolean,
): Promise<PhotoSelectionAlbum> {
  return selectionFetch(token, "/select", { method: "POST", body: { photoId, selected } });
}

/** Khách bấm "chốt danh sách" — sau bước này thợ mới thấy việc. */
export function commitPhotoSelection(token: string): Promise<PhotoSelectionAlbum> {
  return selectionFetch(token, "/commit", { method: "POST" });
}
